'use client'

import React from "react"

import { useState } from 'react'
import { DashboardLayout } from '@/components/dashboard-layout'
import { Plus, TrendingUp, X, Trash2 } from 'lucide-react'

interface Node {
  id: string
  name: string
  ip: string
  type: string
  cpu: number
  memory: number
  containers: number
  status: 'online' | 'offline'
  sshPort: string
  authMethod: string
}

const initialNodes: Node[] = [
  {
    id: '1',
    name: 'X-Node-NY-01',
    ip: '192.168.1.10',
    type: 'BARE METAL',
    cpu: 12,
    memory: 45,
    containers: 12,
    status: 'online',
    sshPort: '22',
    authMethod: 'key',
  },
  {
    id: '2',
    name: 'X-Node-LN-02',
    ip: '45.32.11.90',
    type: 'DIGITALOCEAN',
    cpu: 8,
    memory: 22,
    containers: 5,
    status: 'online',
    sshPort: '22',
    authMethod: 'key',
  },
  {
    id: '3',
    name: 'X-Node-SG-03',
    ip: '104.1.22.8',
    type: 'AWS EC2',
    cpu: 0,
    memory: 0,
    containers: 0,
    status: 'offline',
    sshPort: '22',
    authMethod: 'key',
  },
]

function NodeCard({ node, onDelete }: { node: Node; onDelete: (id: string) => void }) {
  const statusColor = node.status === 'online' ? 'bg-emerald-500' : 'bg-red-500'
  const statusText = node.status === 'online' ? 'Online' : 'Offline'

  return (
    <div className="bg-card border border-border rounded-lg p-6 hover:border-primary/50 transition-all">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className={`w-3 h-3 rounded-full ${statusColor}`} />
          <div>
            <h3 className="text-lg font-semibold text-foreground">{node.name}</h3>
            <p className="text-sm text-muted-foreground">{node.ip}:{node.sshPort}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="px-3 py-1 text-xs font-medium bg-secondary text-foreground rounded">
            {node.type}
          </span>
          <button
            onClick={() => onDelete(node.id)}
            className="p-2 hover:bg-red-500/20 rounded transition-colors"
            title="Delete host"
          >
            <Trash2 size={16} className="text-red-400" />
          </button>
        </div>
      </div>

      <div className="space-y-4">
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-muted-foreground">CPU USAGE</span>
            <span className="text-sm font-semibold text-foreground">{node.cpu}%</span>
          </div>
          <div className="w-full h-2 bg-secondary rounded-full overflow-hidden">
            <div
              className="h-full bg-blue-500 rounded-full transition-all"
              style={{ width: `${Math.min(node.cpu, 100)}%` }}
            />
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-muted-foreground">MEMORY USAGE</span>
            <span className="text-sm font-semibold text-foreground">{node.memory}%</span>
          </div>
          <div className="w-full h-2 bg-secondary rounded-full overflow-hidden">
            <div
              className="h-full bg-emerald-500 rounded-full transition-all"
              style={{ width: `${Math.min(node.memory, 100)}%` }}
            />
          </div>
        </div>

        <div className="pt-4 border-t border-border">
          <p className="text-sm text-foreground font-medium">{node.containers} Active Containers</p>
          <p className="text-xs text-muted-foreground mt-1">Auth: {node.authMethod}</p>
        </div>
      </div>
    </div>
  )
}

export default function NodesPage() {
  const [nodes, setNodes] = useState<Node[]>(initialNodes)
  const [showNewHostForm, setShowNewHostForm] = useState(false)
  const [formData, setFormData] = useState({
    hostName: '',
    ipAddress: '',
    sshPort: '22',
    authMethod: 'key',
  })

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
  }

  const handleSaveHost = () => {
    if (!formData.hostName || !formData.ipAddress) {
      alert('Please fill in all required fields')
      return
    }

    const newNode: Node = {
      id: Date.now().toString(),
      name: formData.hostName,
      ip: formData.ipAddress,
      type: 'CUSTOM',
      cpu: 0,
      memory: 0,
      containers: 0,
      status: 'offline',
      sshPort: formData.sshPort,
      authMethod: formData.authMethod,
    }

    setNodes((prev) => [...prev, newNode])
    console.log('[v0] Host saved:', newNode)
    setFormData({ hostName: '', ipAddress: '', sshPort: '22', authMethod: 'key' })
    setShowNewHostForm(false)
  }

  const handleDeleteHost = (id: string) => {
    setNodes((prev) => prev.filter((node) => node.id !== id))
    console.log('[v0] Host deleted:', id)
  }

  const handleCancel = () => {
    setShowNewHostForm(false)
    setFormData({ hostName: '', ipAddress: '', sshPort: '22', authMethod: 'key' })
  }

  return (
    <DashboardLayout
      title="Infrastructure Nodes"
      subtitle="Manage hosts where Xyberah Agents are running."
      actions={
        <button
          onClick={() => setShowNewHostForm(!showNewHostForm)}
          className="px-4 py-2 bg-primary text-primary-foreground rounded-lg font-medium hover:opacity-90 transition-opacity flex items-center gap-2"
        >
          <Plus size={18} />
          Add New Host
        </button>
      }
    >
      <div className="space-y-6">
        {/* New Host Form */}
        {showNewHostForm && (
          <div className="bg-card border border-primary/50 rounded-lg p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-foreground">Add New Host</h3>
              <button onClick={handleCancel} className="p-2 hover:bg-secondary rounded transition-colors">
                <X size={18} className="text-foreground" />
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-2">Host Name</label>
                <input
                  type="text"
                  name="hostName"
                  value={formData.hostName}
                  onChange={handleInputChange}
                  placeholder="e.g., X-Node-NY-04"
                  className="w-full px-4 py-2 bg-secondary border border-border rounded-lg text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-2">IP Address</label>
                <input
                  type="text"
                  name="ipAddress"
                  value={formData.ipAddress}
                  onChange={handleInputChange}
                  placeholder="e.g., 192.168.1.20"
                  className="w-full px-4 py-2 bg-secondary border border-border rounded-lg text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-2">SSH Port</label>
                <input
                  type="text"
                  name="sshPort"
                  value={formData.sshPort}
                  onChange={handleInputChange}
                  placeholder="e.g., 22"
                  className="w-full px-4 py-2 bg-secondary border border-border rounded-lg text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-2">Auth Method</label>
                <select
                  name="authMethod"
                  value={formData.authMethod}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 bg-secondary border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="key">SSH Key</option>
                  <option value="password">Password</option>
                </select>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={handleSaveHost}
                className="px-6 py-2 bg-primary text-primary-foreground rounded-lg font-medium hover:opacity-90 transition-opacity"
              >
                Save Host
              </button>
              <button
                onClick={handleCancel}
                className="px-6 py-2 border border-border text-foreground rounded-lg font-medium hover:bg-secondary/20 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Nodes Grid */}
        <div className="grid grid-cols-3 gap-6">
          {nodes.map((node) => (
            <NodeCard key={node.id} node={node} onDelete={handleDeleteHost} />
          ))}
        </div>
      </div>
    </DashboardLayout>
  )
}
